import { usePermissions } from '@hooks/usePermissions';
import { Product } from '@interfaces';
import { Skeleton, Typography } from '@mui/material';
import { styled } from '@mui/system';
import { activePrincipleConcentrationMatch } from '../utils/activePrincipleConcentrationMatch';
import { ProductMainCard, Title, TopContainer } from '../styles';

const DetailCardContentSkeleton = () => {
  return (
    <ProductMainCard padding="12px 16px">
      <Skeleton variant="rectangular" width={'100%'} height={68} />
    </ProductMainCard>
  );
};

const ContentContainer = styled('div')<{
  orientation: 'vertical' | 'horizontal';
}>(({ orientation }) => ({
  display: 'flex',
  flexDirection: orientation === 'horizontal' ? 'row' : 'column',
  justifyContent: orientation === 'horizontal' ? 'space-between' : 'flex-start',
  padding: orientation === 'horizontal' ? '6px 24px 12px' : '12px 24px',
}));
const Row = styled('div')`
  margin-bottom: 12px;
  &:last-child {
    margin-bottom: 0;
  }
`;
const RowTitle = styled(Typography)`
  font-size: 13px;
  color: #414046;
`;
const RowContent = styled(Typography)`
  font-size: 13px;
  color: #979797;
`;
interface DetailCardContentProps {
  title: string;
  rows: { title: string; content: string }[];
  orientation?: 'vertical' | 'horizontal';
  loading?: boolean;
  description?: string;
}
export const DetailCardContent = ({
  title,
  rows: inputRows,
  orientation = 'vertical',
  loading,
  description,
}: DetailCardContentProps) => {
  if (loading) return <DetailCardContentSkeleton />;
  return (
    <ProductMainCard>
      <TopContainer>
        <Title variant="h2">{title}</Title>
      </TopContainer>
      {description && (
        <ContentContainer orientation="horizontal">
          <RowContent>{description}</RowContent>
        </ContentContainer>
      )}
      <ContentContainer orientation={orientation}>
        {inputRows.map((row) => {
          return (
            <Row key={row.title}>
              <RowTitle>{row.title}</RowTitle>
              <RowContent>{row.content}</RowContent>
            </Row>
          );
        })}
      </ContentContainer>
    </ProductMainCard>
  );
};

interface DetailCardContentDescriptionProps {
  product: Product;
  orientation?: 'vertical' | 'horizontal';
  loading?: boolean;
}
const DescriptionContent = ({
  product,
  orientation = 'vertical',
  loading,
}: DetailCardContentDescriptionProps) => {
  const { userCan } = usePermissions();
  const isAssistant = userCan('MANAGE_USER');
  const prescriptionType = product.prescriptionType;
  const ispRegister = product.metadata?.ispRegister;
  const activePrinciples = product.activePrinciple;
  const description = product.description;
  const rows = [
    {
      title: 'Condición de venta:',
      content: prescriptionType
        ? {
            HELD: 'Receta retenida',
            CHECK: 'Receta cheque',
            SIMPLE: 'Receta simple' + (isAssistant ? ' ✅' : ''),
            NOT_REQUIRED: 'Receta simple' + (isAssistant ? ' ❌' : ''),
          }[prescriptionType]
        : 'Sin receta',
    },
    ispRegister && {
      title: 'Registro sanitario:',
      content: ispRegister,
    },
    activePrinciples && {
      title: 'Principios activos:',
      get content() {
        return `Cada unidad contiene: ${activePrincipleConcentrationMatch(
          product,
        )}`;
      },
    },
  ].filter(Boolean);

  if (!activePrinciples && !ispRegister && !prescriptionType && !description) {
    return null;
  }

  return (
    <DetailCardContent
      title="Descripción"
      rows={rows}
      orientation={orientation}
      loading={loading}
      description={description}
    />
  );
};

interface DetailCardContentSpecificationsProps {
  product: Product;
  loading?: boolean;
}
const SpecificationsContent = ({
  product,
  loading,
}: DetailCardContentSpecificationsProps) => {
  if (product.prescriptionType) return null;
  const dict = {
    composition: 'Composición',
    indications: 'Indicaciones',
    posology: 'Posología',
    contraindications: 'Contraindicaciones',
    display: 'Presentación',
  };

  const rows = Object.entries(dict)
    .map(([key, title]) => {
      const content = product[key] as string;
      if (!content) return;
      return {
        title,
        content,
      };
    })
    .filter(Boolean);
  if (rows.length === 0) return null;
  return (
    <DetailCardContent title="Ficha técnica" rows={rows} loading={loading} />
  );
};

DetailCardContent.Description = DescriptionContent;
DetailCardContent.Specifications = SpecificationsContent;
